export type Language = 'en' | 'es'

export const translations = {
  en: {
    nav: {
      program: 'Program',
      testimonial: 'Reviews',
      faq: 'FAQ',
      book: 'Book Now',
    },
    hero: {
      headline: 'Speak Spanish Fluently. Faster Than You Thought Possible.',
      subhead:
        'Stop wasting time with unstructured tutoring. It\u2019s a personal 8-week program of live, 1-on-1 video lessons with me \u2014 taking you from beginner to intermediate, or intermediate to fluent.',
      cta: 'Book a Free Session',
      secondaryCta: 'See How It Works',
      trustSignals: [
        'Native Speaker from Paraguay',
        'Live 1-on-1 Video Lessons',
        'Results-Focused Method',
      ],
    },
    painPoints: {
      title: 'Why Most Online Tutoring Fails',
      subtitle:
        'The difference between staying stuck and actually becoming fluent.',
      unstructured: {
        title: 'The "What do you want to learn?" Trap',
        desc: 'Most tutors have no plan. They ask what you want to learn, leaving you confused, overwhelmed, and stuck on a learning plateau for months.',
      },
      structured: {
        title: 'The Step-by-Step Blueprint',
        desc: 'I never ask what you want to learn. I tell you exactly what you need to do next. My structured approach guarantees massive improvements in weeks.',
      },
    },
    program: {
      title: 'The 8-Week Fluency Program',
      subtitle:
        'A proven roadmap delivered through live, 1-on-1 video calls and tailored to your current level \u2014 never a generic, pre-recorded course.',
      track1: {
        title: 'Beginner to Intermediate',
        desc: 'Build a rock-solid foundation. Master essential grammar, everyday vocabulary, and start holding real conversations with confidence.',
      },
      track2: {
        title: 'Intermediate to Fluent',
        desc: 'Break through the plateau. Refine your pronunciation, master complex tenses, and speak naturally like a native.',
      },
    },
    howItWorks: {
      title: 'Your Path to Fluency',
      step1: {
        title: 'Book a Session',
        desc: 'Schedule a quick intro call to assess your current level and goals.',
      },
      step2: {
        title: 'Get Your Custom Plan',
        desc: 'Receive your structured 8-week roadmap tailored specifically for you.',
      },
      step3: {
        title: 'See Fast Results',
        desc: 'Follow the step-by-step method and experience massive improvements in weeks.',
      },
    },
    testimonial: {
      title: "Don't Just Take My Word For It",
      subtitle: 'Hear how Justus broke through his 6-month plateau.',
      quote:
        '"I tried about 8 different online tutors over 6 months and was stuck. They always asked what I wanted to learn, but I didn\'t know! Then I met Mora. She has a structured program and showed me step-by-step what I need to do. Within a couple of weeks, I improved massively and finally broke out of my plateau. I highly recommend working with Mora!"',
      name: 'Justus',
      role: 'Spanish Student',
      watchVideo: 'Watch Video Review',
    },
    booking: {
      title: 'Ready to Start Speaking Spanish?',
      subtitle:
        'Pick a time below to schedule your first session and get your structured plan.',
      price: 'Just $15 / hour',
      freeFirst: 'Your first session is free',
    },
    footer: {
      rights: 'Mora Rebori. All rights reserved.',
    },
    seo: {
      title: 'Learn Spanish Online | Native Tutor Mora Rebori',
      description:
        'Become fluent in Spanish fast with native teacher Mora Rebori. 1-on-1 online lessons & a structured 8-week program. Book your free trial session today.',
      keywords:
        'learn Spanish online, online Spanish lessons, online Spanish tutor, private Spanish lessons, native Spanish teacher, become fluent in Spanish, 1-on-1 Spanish classes, Spanish conversation practice, Spanish for beginners, Spanish lessons for adults',
    },
    faq: {
      title: 'Frequently Asked Questions',
      subtitle:
        'Everything you need to know about learning Spanish online with me.',
      items: [
        {
          q: 'How long does it take to become fluent in Spanish?',
          a: 'While true fluency takes time, my structured 8-week program is designed to get you speaking confidently fast. Most students see massive improvements and break through their learning plateaus within the first few weeks of consistent 1-on-1 Spanish classes.',
        },
        {
          q: 'Do I need any prior Spanish experience?',
          a: 'Not at all! I offer online Spanish lessons for beginners starting from zero, as well as advanced conversation practice for adults looking to perfect their fluency. The program is tailored to your exact starting level.',
        },
        {
          q: 'How are the online Spanish lessons delivered?',
          a: 'All private Spanish lessons are conducted 1-on-1 via video call. This allows for real-time conversation practice, immediate pronunciation feedback, and a highly personalized learning experience with a native Spanish teacher.',
        },
        {
          q: 'What makes this structured Spanish learning program different?',
          a: 'Unlike many online Spanish tutors who just ask "what do you want to learn?", I provide a clear, step-by-step roadmap. You will always know exactly what to study next, eliminating confusion and accelerating your path to fluency.',
        },
        {
          q: 'How do I start my private Spanish lessons?',
          a: 'Simply book a free 30-minute trial session using the calendar above. We will assess your current level, discuss your goals, and I will show you how my structured program can help you become fluent in Spanish fast.',
        },
      ],
    },
  },
  es: {
    nav: {
      program: 'Programa',
      testimonial: 'Reseñas',
      faq: 'FAQ',
      book: 'Reservar',
    },
    hero: {
      headline: 'Habla Español con Fluidez. Más Rápido de lo que Imaginabas.',
      subhead:
        'Deja de perder el tiempo con tutorías sin estructura. Es un programa personal de 8 semanas con clases en vivo 1 a 1 por videollamada conmigo \u2014 que te lleva de principiante a intermedio, o de intermedio a fluido.',
      cta: 'Reserva una Sesión Gratis',
      secondaryCta: 'Ver Cómo Funciona',
      trustSignals: [
        'Hablante Nativa de Paraguay',
        'Clases en Vivo 1 a 1',
        'Método Enfocado en Resultados',
      ],
    },
    painPoints: {
      title: 'Por Qué la Mayoría de las Tutorías Fallan',
      subtitle:
        'La diferencia entre quedarse estancado y volverse realmente fluido.',
      unstructured: {
        title: 'La Trampa de "¿Qué quieres aprender?"',
        desc: 'La mayoría de los tutores no tienen un plan. Te preguntan qué quieres aprender, dejándote confundido y estancado durante meses.',
      },
      structured: {
        title: 'El Plan Paso a Paso',
        desc: 'Nunca te pregunto qué quieres aprender. Te digo exactamente qué necesitas hacer a continuación. Mi enfoque estructurado garantiza mejoras masivas en semanas.',
      },
    },
    program: {
      title: 'El Programa de Fluidez de 8 Semanas',
      subtitle:
        'Una hoja de ruta probada impartida en clases en vivo 1 a 1 por videollamada y adaptada a tu nivel actual \u2014 nunca un curso genérico pregrabado.',
      track1: {
        title: 'Principiante a Intermedio',
        desc: 'Construye una base sólida. Domina la gramática esencial, vocabulario cotidiano y comienza a tener conversaciones reales con confianza.',
      },
      track2: {
        title: 'Intermedio a Fluido',
        desc: 'Supera el estancamiento. Perfecciona tu pronunciación, domina tiempos complejos y habla naturalmente como un nativo.',
      },
    },
    howItWorks: {
      title: 'Tu Camino hacia la Fluidez',
      step1: {
        title: 'Reserva una Sesión',
        desc: 'Programa una breve llamada para evaluar tu nivel actual y tus objetivos.',
      },
      step2: {
        title: 'Obtén tu Plan Personalizado',
        desc: 'Recibe tu hoja de ruta estructurada de 8 semanas diseñada específicamente para ti.',
      },
      step3: {
        title: 'Ve Resultados Rápidos',
        desc: 'Sigue el método paso a paso y experimenta mejoras masivas en semanas.',
      },
    },
    testimonial: {
      title: 'No Solo Confíes en Mi Palabra',
      subtitle: 'Escucha cómo Justus superó su estancamiento de 6 meses.',
      quote:
        '"Probé unos 8 tutores diferentes en 6 meses y estaba estancado. Siempre me preguntaban qué quería aprender, ¡pero yo no sabía! Luego conocí a Mora. Ella tiene un programa estructurado y me mostró paso a paso lo que necesito hacer. En un par de semanas, mejoré masivamente y finalmente salí de mi estancamiento. ¡Recomiendo encarecidamente trabajar con Mora!"',
      name: 'Justus',
      role: 'Estudiante de Español',
      watchVideo: 'Ver Reseña en Video',
    },
    booking: {
      title: '¿Listo para Empezar a Hablar Español?',
      subtitle:
        'Elige un horario a continuación para programar tu primera sesión y obtener tu plan estructurado.',
      price: 'Solo $15 / hora',
      freeFirst: 'Tu primera sesión es gratis',
    },
    footer: {
      rights: 'Mora Rebori. Todos los derechos reservados.',
    },
    seo: {
      title: 'Aprender Español Online | Profesora Nativa Mora Rebori',
      description:
        'Aprende a hablar español con fluidez rápidamente con la profesora nativa Mora Rebori. Clases particulares online y un programa estructurado de 8 semanas. Reserva tu sesión de prueba gratis hoy.',
      keywords:
        'aprender español online, clases de español online, profesora de español nativa, clases particulares de español, hablar español con fluidez, español para principiantes, clases de conversación en español, profesora de español online',
    },
    faq: {
      title: 'Preguntas Frecuentes',
      subtitle:
        'Todo lo que necesitas saber sobre aprender español online conmigo.',
      items: [
        {
          q: '¿Cuánto tiempo se tarda en hablar español con fluidez?',
          a: 'Aunque la fluidez real lleva tiempo, mi programa estructurado de 8 semanas está diseñado para que hables con confianza rápidamente. La mayoría de los estudiantes ven mejoras masivas y superan su estancamiento en las primeras semanas de clases particulares de español.',
        },
        {
          q: '¿Necesito experiencia previa en español?',
          a: '¡Para nada! Ofrezco clases de español online para principiantes desde cero, así como clases de conversación avanzadas para adultos que buscan perfeccionar su fluidez. El programa se adapta a tu nivel inicial exacto.',
        },
        {
          q: '¿Cómo se imparten las clases de español online?',
          a: 'Todas las clases particulares de español se realizan 1 a 1 por videollamada. Esto permite practicar la conversación en tiempo real, recibir comentarios inmediatos sobre la pronunciación y tener una experiencia de aprendizaje muy personalizada con una profesora de español nativa.',
        },
        {
          q: '¿Qué hace diferente a este programa estructurado para aprender español?',
          a: 'A diferencia de muchos tutores de español online que solo preguntan "¿qué quieres aprender?", yo proporciono una hoja de ruta clara paso a paso. Siempre sabrás exactamente qué estudiar a continuación, eliminando la confusión y acelerando tu camino hacia la fluidez.',
        },
        {
          q: '¿Cómo empiezo mis clases particulares de español?',
          a: 'Simplemente reserva una sesión de prueba gratuita de 30 minutos usando el calendario de arriba. Evaluaremos tu nivel actual, discutiremos tus objetivos y te mostraré cómo mi programa estructurado puede ayudarte a hablar español con fluidez rápido.',
        },
      ],
    },
  },
}
